import React from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import ImageBrowser from '../../shared/ImageBrowser';
import { useUserImages } from '../../../services/userImageService';
import { useVendorSubscriptionStatus } from '../../../services/subscriptionService';

const PortfolioTab = () => {
  const { data: statusPayload, isLoading: subscriptionLoading, isError: subscriptionError } =
    useVendorSubscriptionStatus();

  const subscriptionReady = !subscriptionLoading && statusPayload?.success === true;
  const hasActiveSubscription = statusPayload?.data?.has_active_subscription === true;
  const currentSubscription = statusPayload?.data?.current_subscription;
  const maxPortfolioItems = currentSubscription?.limits?.max_portfolio_items;

  const { data: imagesResponse, isLoading: imagesLoading } = useUserImages({ image_type: 'portfolio' });
  const galleryPortfolioCount = imagesResponse?.data?.length ?? 0;
  const usagePortfolioCount = currentSubscription?.usage?.portfolio_count;
  const portfolioCount =
    subscriptionReady && typeof usagePortfolioCount === 'number' ? usagePortfolioCount : galleryPortfolioCount;

  let maxImagesForBrowser = null;
  if (subscriptionReady) {
    if (!hasActiveSubscription) {
      maxImagesForBrowser = 0;
    } else if (maxPortfolioItems != null) {
      maxImagesForBrowser = maxPortfolioItems;
    } else {
      maxImagesForBrowser = null;
    }
  }

  const readyForLimitCopy = subscriptionReady && !imagesLoading;

  const remainingSlots =
    hasActiveSubscription && maxPortfolioItems != null
      ? Math.max(0, maxPortfolioItems - portfolioCount)
      : null;

  const atPlanLimit =
    hasActiveSubscription && maxPortfolioItems != null && remainingSlots === 0;

  let statusLine = null;
  if (readyForLimitCopy) {
    if (!hasActiveSubscription) {
      statusLine = (
        <>
          Portfolio uploads need an active subscription.{' '}
          <Link to="/vendor/subscription" className="text-primary-600 font-medium underline hover:text-primary-700">
            View plans
          </Link>
          .
        </>
      );
    } else if (maxPortfolioItems == null) {
      statusLine = `Your plan allows unlimited portfolio images. ${portfolioCount} uploaded.`;
    } else if (remainingSlots === 0) {
      statusLine = `You've reached your plan limit (${maxPortfolioItems} images). Delete one to add another, or upgrade for a higher limit.`;
    } else {
      statusLine = `${remainingSlots} ${remainingSlots === 1 ? 'image' : 'images'} remaining to upload (${portfolioCount}/${maxPortfolioItems} used on your plan).`;
    }
  }

  let guidelinesLimitLine = null;
  if (subscriptionError && !subscriptionLoading) {
    guidelinesLimitLine = <>Could not load your subscription. Refresh the page if portfolio limits look wrong.</>;
  } else if (!subscriptionReady) {
    guidelinesLimitLine = <>Checking your subscription for portfolio limits…</>;
  } else if (!hasActiveSubscription) {
    guidelinesLimitLine = <>Active subscription required — portfolio limits are set by your plan.</>;
  } else if (maxPortfolioItems == null) {
    guidelinesLimitLine = <>Your plan includes unlimited portfolio images.</>;
  } else if (!imagesLoading) {
    guidelinesLimitLine = (
      <>
        Your plan allows up to {maxPortfolioItems} portfolio images ({remainingSlots} slot
        {remainingSlots === 1 ? '' : 's'} left).
      </>
    );
  } else {
    guidelinesLimitLine = <>Your plan allows up to {maxPortfolioItems} portfolio images.</>;
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Portfolio Images</h3>
        <p className="text-sm text-gray-600">
          Upload and manage portfolio images shown on your public vendor profile. Limits here are separate from{' '}
          <strong>general images</strong> (Photo Gallery tab)—each follows your plan&apos;s own cap.
        </p>
        {statusLine && (
          <p
            className={`text-sm font-medium mt-2 ${atPlanLimit || (!hasActiveSubscription && readyForLimitCopy) ? 'text-amber-700' : 'text-primary-700'}`}
          >
            {statusLine}
          </p>
        )}
      </div>

      <div className="bg-gray-50 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <h4 className="text-sm font-medium text-blue-900">Portfolio Guidelines</h4>
            <ul className="text-sm text-blue-700 mt-1 space-y-1 list-disc list-inside">
              <li>{guidelinesLimitLine}</li>
              <li>Supported formats: JPEG, PNG, GIF, WebP</li>
              <li>Maximum file size: 10MB per image</li>
              <li>Recommended dimensions: 1200x800px or larger</li>
              <li>Showcase your best work to attract clients</li>
            </ul>
          </div>
        </div>
      </div>

      <ImageBrowser
        imageType="portfolio"
        onSelectImage={null}
        showUpload={true}
        showDelete={true}
        showSetDefault={false}
        maxImages={maxImagesForBrowser}
      />
    </div>
  );
};

export default PortfolioTab;
